/**
 * B2B newsletter signup.
 *
 * Posts straight to the HubSpot Forms API so a trade subscriber lands in the
 * same CRM as every quote enquiry, with marketing consent recorded against the
 * contact rather than inferred.
 */
class CommercialSignup extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-signup-form]');
    this.email = this.querySelector('[data-signup-email]');
    this.message = this.querySelector('[data-signup-message]');
    this.section = this.closest('.shopify-section') || this;
  }

  connectedCallback() {
    if (!this.form) return;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  setting(name, fallback) {
    return this.dataset[name] || fallback;
  }

  say(text, ok) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = false;
    this.message.classList.toggle('is-error', !ok);
  }

  async onSubmit(event) {
    event.preventDefault();
    const value = (this.email.value || '').trim();

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      return this.say(this.dataset.invalidMessage || 'Check the email address.', false);
    }

    const portal = this.dataset.portalId;
    const guid = this.dataset.formGuid;
    if (!portal || !guid) {
      // No form behind the field yet. Say that, rather than reporting it as a
      // failed submission: the two need different fixes and the generic error
      // sent people looking at the wrong one.
      console.warn('[commercial-signup] no HubSpot form set. Add the newsletter form GUID in the section settings.');
      return this.say(
        this.dataset.unconfiguredMessage || 'Signup is not connected yet.',
        false
      );
    }

    const host = this.dataset.region === 'eu1' ? 'api-eu1.hsforms.com' : 'api.hsforms.com';
    const hutk = (document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]*)/) || [])[1];

    const payload = {
      fields: [{ name: 'email', value }],
      context: { pageUri: window.location.href, pageName: document.title },
      legalConsentOptions: {
        consent: {
          consentToProcess: true,
          text: 'By subscribing, you agree to receive marketing emails from FÔLD Reformer.',
        },
      },
    };
    if (hutk) payload.context.hutk = hutk;

    try {
      const res = await fetch(
        `https://${host}/submissions/v3/integration/submit/${portal}/${guid}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
      );
      if (!res.ok) {
        // HubSpot says why (a field not on the form, a blocked address), which
        // the status alone does not.
        const detail = await res.text().catch(() => '');
        throw new Error(`HubSpot responded ${res.status}: ${detail.slice(0, 300)}`);
      }

      this.say(this.dataset.successMessage || 'Thanks, you are on the list.', true);
      this.form.reset();

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'commercial_newsletter_signup', page_path: window.location.pathname });
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'commercial_newsletter_signup', { page_path: window.location.pathname });
      }
    } catch (error) {
      console.error('[commercial-signup]', error);
      this.say(this.dataset.errorMessage || 'That did not go through.', false);
    }
  }
}

if (!customElements.get('commercial-signup')) {
  customElements.define('commercial-signup', CommercialSignup);
}
